import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from 'components';
import { FormStepContentProps } from 'types';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { useContentContext } from '@qbe/dfc-content';

import PersonalDetails from './sections/PersonalDetails';
import ContactDetails from './sections/ContactDetails';
import validationSchema, { personalDetailsSchema } from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useIsFieldRequired } from 'utils/isFieldRequired';
import { useContentComplaintPartners } from 'utils/content';

const YourDetailsForm = ({ onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData } = useFormState();

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  // onTouched:
  // Initial validation occurs on the first blur event.
  // Subsequent validation happens on every change event.
  // Useful when you want to validate after the user interacts with the input.
  const form = useForm<FormDataType>({
    mode: 'onTouched',
    defaultValues: formData,
    resolver: yupResolver(validationSchema) as any,
  });

  const { isFieldRequired } = useIsFieldRequired(form, validationSchema);

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const values = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const isContactDetailsValid = validationSchema.isValidSync(watch());

  const [isContactDetailsDisplayed, setIsContactDetailsDisplayed] = React.useState(false);
  const [showBtnContactDetails, setShowBtnContactDetails] = React.useState(true);

  const isPersonalDetailsValid = personalDetailsSchema.isValidSync(values);
  const [showPersonalDetailsButton, setShowPersonalDetailsButton] = React.useState(true);

  const handleSubmitPersonalDetails = () => {
    setShowPersonalDetailsButton(false);
    setShowBtnContactDetails(true);
    setIsContactDetailsDisplayed(true);
  };

  return (
    <Form onSubmit={handleSubmit(onComplete)} ref={formRef} data-test="your-details-form">
      <FormCard variant="pageTitle" title={getTextContent('breach.your.details.header')} description="" />

      <PersonalDetails
        form={form}
        isFieldRequired={isFieldRequired}
        isValid={isPersonalDetailsValid}
        showNextButton={showPersonalDetailsButton}
        onSubmit={handleSubmitPersonalDetails}
      />

      <ContactDetails
        form={form}
        isFieldRequired={isFieldRequired}
        isValid={isContactDetailsValid}
        showNextButton={showBtnContactDetails}
        show={isContactDetailsDisplayed}
      />
    </Form>
  );
};

export default YourDetailsForm;
